import React, { useEffect, useState } from 'react'
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  TablePagination,
  Tooltip,
  Typography,
} from '@material-ui/core'
import SettingsIcon from '@material-ui/icons/Settings'
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined'
import FullscreenIcon from '@material-ui/icons/Fullscreen'
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import ThumbUpIcon from '@material-ui/icons/ThumbUp'
import ThumbDownIcon from '@material-ui/icons/ThumbDown'
import RateReviewOutlinedIcon from '@material-ui/icons/RateReviewOutlined'
import CloseIcon from '@material-ui/icons/Close'
import axios from 'axios'
import moment from 'moment'
import { REST_API_END_POINT } from 'src/constants/DefaultValues'
import { WA } from './whatsappTheme'
import defaultAvatar from '../../../assets/defaultAvatar.png'

const REVIEWS_PER_PAGE = 10

function ChatWidgetSettings(props) {
  const tone = props.tone || 'light'
  const maximized = Boolean(props.maximized)
  const notificationsEnabled = props.notificationsEnabled !== false
  const onToggleMaximize = props.onToggleMaximize
  const onToggleNotifications = props.onToggleNotifications
  const onDownloadTranscript = props.onDownloadTranscript
  const showReviews = Boolean(props.showReviews)
  const [anchorEl, setAnchorEl] = useState(null)
  const [reviewsOpen, setReviewsOpen] = useState(false)
  const [reviewsLoading, setReviewsLoading] = useState(false)
  const [reviews, setReviews] = useState([])
  const [reviewsTotal, setReviewsTotal] = useState(0)
  const [reviewsPage, setReviewsPage] = useState(0)
  const menuOpen = Boolean(anchorEl)
  const iconColor = tone === 'light' ? '#fff' : WA.meta

  const closeMenu = () => setAnchorEl(null)

  const loadReviews = async (pageIndex = 0) => {
    setReviewsLoading(true)
    try {
      const response = await axios.post(`${REST_API_END_POINT}chat/list-ratings`, {
        limit: REVIEWS_PER_PAGE,
        page: pageIndex + 1,
      })
      if (response.data?.status === 1) {
        setReviews(response.data.data || [])
        setReviewsTotal(Number(response.data.total) || 0)
        setReviewsPage(pageIndex)
      } else {
        setReviews([])
        setReviewsTotal(0)
      }
    } catch (error) {
      console.log('Error loading chat reviews', error)
      setReviews([])
      setReviewsTotal(0)
    } finally {
      setReviewsLoading(false)
    }
  }

  useEffect(() => {
    if (reviewsOpen) {
      setReviewsPage(0)
      loadReviews(0)
    }
  }, [reviewsOpen])

  return (
    <>
      <Tooltip title="Settings">
        <IconButton
          size="small"
          aria-label="Chat settings"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            color: iconColor,
            width: 36,
            height: 36,
            p: 0,
            flexShrink: 0,
            bgcolor: 'transparent',
            ...(tone === 'light'
              ? {
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                }
              : {
                  '&:hover': { bgcolor: 'rgba(2, 125, 194, 0.08)' },
                }),
          }}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ zIndex: 1600 }}
        ModalProps={{ style: { zIndex: 1600 } }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 268,
            borderRadius: '12px',
            boxShadow: WA.shadow,
            border: `1px solid ${WA.border}`,
            py: 0.5,
            zIndex: 1600,
          },
        }}
      >
        {showReviews ? (
          <MenuItem
            onClick={() => {
              closeMenu()
              setReviewsOpen(true)
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: WA.meta }}>
              <RateReviewOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Chat reviews"
              primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: WA.text } }}
            />
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            if (onDownloadTranscript) onDownloadTranscript()
            closeMenu()
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: WA.meta }}>
            <DescriptionOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Download chat transcript"
            primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: WA.text } }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (onToggleMaximize) onToggleMaximize()
            closeMenu()
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: WA.meta }}>
            {maximized ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={maximized ? 'Restore window' : 'Maximize window'}
            primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: WA.text } }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (onToggleNotifications) onToggleNotifications(!notificationsEnabled)
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: WA.meta }}>
            <VolumeUpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Notifications"
            primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 600, color: WA.text } }}
          />
          <Switch
            size="small"
            color="primary"
            checked={Boolean(notificationsEnabled)}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation()
              if (onToggleNotifications) onToggleNotifications(event.target.checked)
            }}
          />
        </MenuItem>
      </Menu>

      <Dialog
        open={reviewsOpen}
        onClose={() => setReviewsOpen(false)}
        maxWidth="xs"
        fullWidth
        style={{ zIndex: 1700 }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            border: `1px solid ${WA.border}`,
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            position: 'relative',
            pr: 6,
            fontWeight: 700,
            color: WA.text,
          }}
        >
          Chat reviews
          <IconButton
            size="small"
            onClick={() => setReviewsOpen(false)}
            aria-label="Close"
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 0, py: 0 }}>
          {reviewsLoading ? (
            <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} sx={{ color: WA.header }} />
            </Box>
          ) : reviews.length === 0 ? (
            <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: WA.meta }}>
                No customer reviews yet.
              </Typography>
            </Box>
          ) : (
            <>
              <List disablePadding>
                {reviews.map((item) => {
                  const isUp = item.rating === 'up'
                  const isGroup = Number(item.conversation_type) === 2
                  const chatKind = isGroup ? 'Group chat' : 'Single chat'
                  const when = item.created_at
                    ? moment.unix(item.created_at).format('D MMM YYYY, h:mm A')
                    : ''
                  const secondaryParts = [
                    chatKind,
                    isGroup && item.group_name ? item.group_name : null,
                    when || null,
                  ].filter(Boolean)
                  return (
                    <ListItem
                      key={item.id}
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        borderBottom: `1px solid ${WA.border}`,
                        alignItems: 'flex-start',
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 48 }}>
                        <Avatar
                          src={item.profile_pic || defaultAvatar}
                          alt={item.rater_name}
                          sx={{ width: 36, height: 36 }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: WA.text }}>
                              {item.rater_name || 'Customer'}
                            </Typography>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 8,
                                bgcolor: isUp
                                  ? 'rgba(46, 125, 50, 0.1)'
                                  : 'rgba(211, 47, 47, 0.08)',
                                color: isUp ? '#2E7D32' : '#C62828',
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              {isUp ? (
                                <ThumbUpIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <ThumbDownIcon sx={{ fontSize: 14 }} />
                              )}
                              {isUp ? 'Helpful' : 'Not helpful'}
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: WA.meta, display: 'block', mt: 0.5 }}>
                            {secondaryParts.join(' · ') || 'Unknown time'}
                          </Typography>
                        }
                      />
                    </ListItem>
                  )
                })}
              </List>
              {reviewsTotal > REVIEWS_PER_PAGE ? (
                <TablePagination
                  component="div"
                  count={reviewsTotal}
                  page={reviewsPage}
                  onPageChange={(_event, nextPage) => loadReviews(nextPage)}
                  rowsPerPage={REVIEWS_PER_PAGE}
                  rowsPerPageOptions={[REVIEWS_PER_PAGE]}
                  labelRowsPerPage=""
                  sx={{
                    borderTop: `1px solid ${WA.border}`,
                    '.MuiTablePagination-toolbar': {
                      minHeight: 48,
                      px: 1,
                    },
                    '.MuiTablePagination-displayedRows': {
                      color: WA.meta,
                      fontSize: 12,
                    },
                  }}
                />
              ) : null}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ChatWidgetSettings
